/**
 * Date utility functions
 * Handles timezone-safe date operations
 */

/**
 * Format a Date object to YYYY-MM-DD in local timezone
 * This avoids timezone conversion issues with toISOString()
 * 
 * @param {Date} date - The date to format
 * @returns {string} Date in YYYY-MM-DD format
 * 
 * @example
 * const date = new Date('2026-05-17T00:00:00');
 * formatDateLocal(date); // Returns "2026-05-17" (not "2026-05-16")
 */
export const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get current date in YYYY-MM-DD format (local timezone)
 * 
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayLocal = () => {
  return formatDateLocal(new Date());
};

/**
 * Add days to a date and return YYYY-MM-DD in local timezone
 * 
 * @param {Date} date - The starting date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} Date in YYYY-MM-DD format
 */
export const addDaysLocal = (date, days) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return formatDateLocal(newDate);
};

/**
 * Format a date for display
 * 
 * @param {Date|string} date - Date object or ISO string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateDisplay = (date, options = {}) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(undefined, options);
};

/**
 * Parse YYYY-MM-DD string to Date object at midnight local time
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date} Date object at midnight local time
 */
export const parseDateLocal = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

